import React, { useEffect, useContext, useRef } from "react";
import Modal from "react-modal";
import CloseButton from "../../commonComponents/CloseButton";
import CustomButton from "../../commonComponents/CustomButton";
import HtmlTooltip from "../../commonComponents/HtmlTooltip";
import { WebSocketContext } from "../../../context/WebSocketContext";
import { AuthContext } from "../../../context/AuthContext";
import { GeneralContext } from "../../../context/GeneralContext";
import { DashboardContext } from "../../../context/DashboardContext";
import { useTutorialProgress } from "../../../context/TutorialContext";
import {
  hasProperty,
  processSymbols,
  generateWarningObject,
  convertDataFromUnitsToUnits
} from "../../../utils/helpers";
import units from "../../../utils/units";
import { useState } from "react";
import "./MaterialDBModal.scss";
import { deepCopy } from "../Models/modelLogic";

export default function MaterialDBModal({
  isOpen,
  onClose,
  appElement,
  getModelFromFromFilesById,
  addModelEntryFromFile,
}) {
  const {
    sendJsonMessage,
    lastJsonMessage,
    retrieveRequestData,
    deleteRequestDataEntry,
  } = useContext(WebSocketContext);
  const { currentUser, checkAccessLevel } = useContext(AuthContext);
  const hasSentRequestRef = useRef(false);
  const { recordedErrorLog, limitedToast } = useContext(GeneralContext);
  const { monitorStateChange } = useTutorialProgress();
  const {
    allLocalModels,
    fileID,
    setFileID,
    setUploadedFiles,
    uploadedFiles,
    setWarnings,
    setNewWarningCount,
    namingDic,
    setNamingDic,
    defaultXAxisUnit,
  } = useContext(DashboardContext);
  const [materialList, setMaterialList] = useState([]);
  const [materialInstanceList, setMaterialInstanceList] = useState([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [selectedMaterialInstanceId, setSelectedMaterialInstanceId] =
    useState(null);
  const [validationDataList, setValidationDataList] = useState([]);
  const [selectedValidation, setSelectedValidation] = useState(null);
  const [validationDetailModalOpen, setValidationDetailModalOpen] =
    useState(false);

  // Helper function to modify links to open in new tab
  const processLinksForNewTab = (htmlString) => {
    if (!htmlString) return htmlString;
    // Use regex to find all <a> tags and add target="_blank" and rel="noopener noreferrer"
    return htmlString.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
      let updatedAttributes = attributes;

      // Replace or add target="_blank"
      if (/target\s*=\s*["'][^"']*["']/i.test(updatedAttributes)) {
        updatedAttributes = updatedAttributes.replace(
          /target\s*=\s*["'][^"']*["']/gi,
          'target="_blank"'
        );
      } else {
        updatedAttributes = `${updatedAttributes} target="_blank"`;
      }

      // Replace or add rel="noopener noreferrer"
      if (/rel\s*=\s*["'][^"']*["']/i.test(updatedAttributes)) {
        updatedAttributes = updatedAttributes.replace(
          /rel\s*=\s*["']([^"']*?)["']/gi,
          (relMatch, relValue) => {
            let newRel = relValue;
            if (!newRel.includes("noopener")) {
              newRel = `${newRel} noopener`.trim();
            }
            if (!newRel.includes("noreferrer")) {
              newRel = `${newRel} noreferrer`.trim();
            }
            return `rel="${newRel}"`;
          }
        );
      } else {
        updatedAttributes = `${updatedAttributes} rel="noopener noreferrer"`;
      }

      return `<a ${updatedAttributes}>`;
    });
  };

  const getValidationData = (validation) => {
    try {
      // Parse datafile to get coordinates
      const datafile = JSON.parse(validation.datafile);
      const coordinates = datafile.coordinates || [];

      // Calculate min, max, npoints from coordinates
      const xValues = coordinates.map((coord) => coord.x);
      const min = xValues.length > 0 ? Math.min(...xValues) : 0;
      const max = xValues.length > 0 ? Math.max(...xValues) : 0;
      const npoints = coordinates.length;

      // Get quantity name from allLocalModels
      let quantityName = "";
      if (allLocalModels && allLocalModels.length > 0) {
        const model = allLocalModels.find(
          (m) => m.speqqle_id === validation.model_type
        );
        if (model && model.outputs) {
          const output = model.outputs.find(
            (o) => o.speqqle_id === validation.quantity
          );
          if (output) {
            quantityName = output.name;
          }
        }
      }

      return {
        name: validation.name,
        min,
        max,
        units: validation.units,
        npoints,
        quantityName,
        publication: validation.publication
          ? processLinksForNewTab(validation.publication)
          : null,
      };
    } catch (error) {
      recordedErrorLog("Error formatting validation entry:", error);
      return {
        name: validation.name,
        min: 0,
        max: 0,
        units: "",
        npoints: 0,
        quantityName: "",
        publication: null,
      };
    }
  };

  const handleMaterialClick = (id) => {
    try {
      if (!currentUser || !currentUser.id) return;
      setSelectedMaterialId(id);
      setSelectedMaterialInstanceId(null); // Reset instance selection when material changes
      setValidationDataList([]); // Clear validation data when material changes
      sendJsonMessage(
        { User: currentUser.id, Material: { GetMaterialInstanceList: id } },
        { type: "get-material-instance-list", materialId: id }
      );
    } catch (error) {
      recordedErrorLog("Material instance list request failed:", error);
    }
  };

  const handleInstanceClick = (id) => {
    try {
      if (!currentUser || !currentUser.id) return;
      setSelectedMaterialInstanceId(id);
      sendJsonMessage(
        { User: currentUser.id, Material: { GetValidationDataList: id } },
        { type: "get-validation-data-list", instanceId: id }
      );
    } catch (error) {
      recordedErrorLog("Validation data list request failed:", error);
    }
  };

  const handleValidationClick = (validation) => {
    setSelectedValidation(validation);
    setValidationDetailModalOpen(true);
  };

  const handleCloseValidationDetail = () => {
    setValidationDetailModalOpen(false);
    setSelectedValidation(null);
  };

  const handleLoadDataset = () => {
    try {
      if (!selectedValidation) return;

      // Check if a file with this name already exists
      const existingFile = uploadedFiles.find(
        (file) => file.name === selectedValidation.name
      );
      if (existingFile) {
        limitedToast(
          `A file with the name "${selectedValidation.name}" already exists. Please rename or remove the existing file first.`
        );
        generateWarningObject(
          `A file with the name "${selectedValidation.name}" already exists. Please rename or remove the existing file first.`,
          2,
          setWarnings,
          setNewWarningCount
        );
        return;
      }

      // Parse datafile to get coordinates
      const datafile = JSON.parse(selectedValidation.datafile);
      const coordinates = datafile.coordinates || [];

      if (coordinates.length === 0) {
        limitedToast("No data points available in this validation dataset");
        return;
      }

      // Convert units string to unit object format
      const unitsString = selectedValidation.units || "";
      const matchedUnit =
        units.find((u) => u.value === unitsString) || units[0]; // Default to first unit (cm^-1) if not found

      // Create a unit object for conversion (anyUnitToCm1 expects unit.name as a string)
      // Map unit.value to the name string expected by anyUnitToCm1
      // Convert x coordinates to cm^-1 if units are not already cm^-1
      const dataPoints = convertDataFromUnitsToUnits(matchedUnit, units[0], coordinates, true);
      // Calculate exact min and max X values (replicating createFileData logic)
      const maxX = dataPoints.reduce((max, p) => {
        return p.x > max ? p.x : max;
      }, dataPoints[0]?.x || 0);

      const minX = dataPoints.reduce((min, p) => {
        return p.x < min ? p.x : min;
      }, dataPoints[0]?.x || 0);

      // Create file data structure (replicating createFileData pattern)
      // Note: Coordinates are converted to cm^-1 internally, but xUnit stores the original unit
      const fileData = {
        name: selectedValidation.name,
        size: 0, // Not applicable for validation data
        type: "text/plain",
        dataPoints: dataPoints,
        npoints: dataPoints.length,
        edges: [{ min: "", max: "", unit: defaultXAxisUnit }],
        dataRangeMin: minX,
        dataRangeMax: maxX,
        xUnit: matchedUnit, // Store the original unit from the validation data
        ID: fileID,
        options: {
          xUnit: matchedUnit,
        }, // Empty since this is pre-processed data
      };

      // Add to uploadedFiles and increment ID
      setUploadedFiles((prevFiles) => [...prevFiles, fileData]);
      const selectedInstance = materialInstanceList.find(
        (inst) => inst.id === selectedMaterialInstanceId
      );
      const selectedMaterial = materialList.find(
        (mat) => mat.id === selectedMaterialId
      );
      monitorStateChange("data_file_from_material_db_added", {
        fileName: selectedValidation.name,
        materialInstance: selectedInstance.name,
        material: selectedMaterial.name,
        fileData: fileData,
      });
      setFileID((old) => old + 1);

      // Send monitoring message to backend
      try {
        if (currentUser && currentUser.id) {
          // Find the selected material instance
          const selectedInstance = materialInstanceList.find(
            (inst) => inst.id === selectedMaterialInstanceId
          );
          const selectedMaterial = materialList.find(
            (mat) => mat.id === selectedMaterialId
          );

          sendJsonMessage(
            {
              User: currentUser.id,
              Monitoring: {
                MaterialDB: [
                  {
                    action: "LoadDataset",
                    datasetName: selectedValidation.name,
                    materialInstance: selectedInstance?.name || "",
                    material: selectedMaterial?.name || "",
                  },
                ],
              },
            },
            false
          );
        }
      } catch (error) {
        console.warn(
          "Failed to send MaterialDB LoadDataset monitoring message:",
          error
        );
      }

      // Close modal
      handleCloseValidationDetail();
    } catch (error) {
      recordedErrorLog("Error loading validation dataset:", error);
      limitedToast("Failed to load dataset");
    }
  };

  const handleLoadModelFromMaterialDB = () => {
    try {
      if (!selectedMaterialInstanceId) {
        limitedToast("Please select a material instance first");
        return;
      }

      // Find the selected material instance
      const selectedInstance = materialInstanceList.find(
        (inst) => inst.id === selectedMaterialInstanceId
      );

      if (!selectedInstance || !selectedInstance.speqqle_model) {
        limitedToast("No model data available for this material instance");
        generateWarningObject(
          "No model data available for this material instance",
          2,
          setWarnings,
          setNewWarningCount
        );
        return;
      }

      // Parse the speqqle_model content (same as processFiles in Models.js)
      const listOfModelsToParse = selectedInstance.speqqle_model.split(
        "###MODEL_SEPARATOR###"
      );
      const parsedModels = [];

      for (let i = 0; i < listOfModelsToParse.length; i++) {
        const modelToParse = listOfModelsToParse[i];
        parsedModels.push(JSON.parse(modelToParse));
      }

      // We select the first model in the file because we are always putting main model first when saving
      const mainModel = parsedModels[0];

      if (allLocalModels.length > 0) {
        if (
          allLocalModels.some(
            (model) => model.speqqle_id === mainModel.modeltype
          )
        ) {
          // Use the same function as Load Experiment
          const generatedModel = getModelFromFromFilesById(
            mainModel.modelid,
            parsedModels,
            mainModel.modeltype
          );
          let desiredName = selectedInstance.name;

          // Check if the name already exists in namingDic
          const existingNames = Object.values(namingDic);
          if (existingNames.includes(desiredName)) {
            // Name exists, find a unique name by appending a number
            let counter = 1;
            let uniqueName = `${desiredName}_${counter}`;
            while (existingNames.includes(uniqueName)) {
              counter++;
              uniqueName = `${desiredName}_${counter}`;
            }
            generatedModel.displayName = uniqueName;
          } else {
            generatedModel.displayName = desiredName;
          }

          setNamingDic((prev) => ({
            ...prev,
            [generatedModel.FE_ID]: generatedModel.displayName,
          }));
          addModelEntryFromFile(generatedModel);
          const selectedMaterial = materialList.find(
            (mat) => mat.id === selectedMaterialId
          );
          monitorStateChange("model_added_from_material_db", {
            modeltype: generatedModel.speqqleID,
            materialInstance: selectedInstance.name,
            material: selectedMaterial.name,
            modelID: generatedModel.FE_ID,
            model: generatedModel,
          });

          // Send monitoring message to backend
          try {
            if (currentUser && currentUser.id) {
              const selectedMaterial = materialList.find(
                (mat) => mat.id === selectedMaterialId
              );

              sendJsonMessage(
                {
                  User: currentUser.id,
                  Monitoring: {
                    MaterialDB: [
                      {
                        action: "LoadModel",
                        modelName: desiredName,
                        materialInstance: selectedInstance?.name || "",
                        material: selectedMaterial?.name || "",
                      },
                    ],
                  },
                },
                false
              );
            }
          } catch (error) {
            console.warn(
              "Failed to send MaterialDB LoadModel monitoring message:",
              error
            );
          }

          onClose(); // Close the Material DB modal after successful load
        } else {
          limitedToast("Model authentication issue.");
          generateWarningObject(
            "This model could not be found in your subscribed model list. If you believe this is a mistake, please contact support.",
            2,
            setWarnings,
            setNewWarningCount
          );
        }
      } else {
        limitedToast(
          "Model list not loaded yet. Please try reloading the page."
        );
        generateWarningObject(
          "Model list not loaded yet. Please try reloading the page.",
          1,
          setWarnings,
          setNewWarningCount
        );
      }
    } catch (error) {
      recordedErrorLog(
        "Error loading model from Material DB:",
        error,
        true,
        "Failed to load model"
      );
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen && currentUser && currentUser.id && !hasSentRequestRef.current) {
      hasSentRequestRef.current = true;
      sendJsonMessage(
        { User: currentUser.id, Material: { GetMaterialList: [] } },
        { type: "get-material-list" }
      );
    }
    if (!isOpen) {
      hasSentRequestRef.current = false;
      setSelectedMaterialId(null);
      setSelectedMaterialInstanceId(null);
      setMaterialInstanceList([]);
      setValidationDataList([]);
    }
  }, [isOpen, currentUser, sendJsonMessage]);

  // Handle response for get-material-list
  useEffect(() => {
    try {
      if (!isOpen) return;
      if (lastJsonMessage && hasProperty(lastJsonMessage, "requestID")) {
        const requestData = retrieveRequestData(lastJsonMessage.requestID);
        if (requestData && requestData.type === "get-material-list") {
          // Handle warnings/errors similar to other responses
          if (
            Array.isArray(lastJsonMessage.Error) &&
            lastJsonMessage.Error.length > 0
          ) {
            recordedErrorLog(
              "Material list error:",
              lastJsonMessage.Error,
              true,
              "Backend Error"
            );
          }
          if (
            Array.isArray(lastJsonMessage.Warning) &&
            lastJsonMessage.Warning.length > 0
          ) {
            recordedErrorLog("Material list warning:", lastJsonMessage.Warning);
            if (limitedToast) {
              limitedToast("Received warnings from material list request");
            }
          }

          const materials =
            hasProperty(lastJsonMessage, "Material") &&
            hasProperty(lastJsonMessage.Material, "MaterialList")
              ? lastJsonMessage.Material.MaterialList
              : [];
          setMaterialList(Array.isArray(materials) ? materials : []);
          deleteRequestDataEntry(lastJsonMessage.requestID);
        }
      }
    } catch (error) {
      recordedErrorLog("Error handling get-material-list response:", error);
    }
  }, [
    lastJsonMessage,
    isOpen,
    retrieveRequestData,
    deleteRequestDataEntry,
    recordedErrorLog,
    limitedToast,
  ]);

  // Auto-select first material with proper access level when list loads
  useEffect(() => {
    if (
      isOpen &&
      materialList.length > 0 &&
      selectedMaterialId === null &&
      checkAccessLevel &&
      currentUser &&
      currentUser.id
    ) {
      // Find first material with proper access level
      const sortedMaterials = [...materialList].sort(
        (a, b) => (a.list_index ?? 0) - (b.list_index ?? 0)
      );
      const firstAccessibleMaterial = sortedMaterials.find(
        (mat) =>
          hasProperty(mat, "access_level") && checkAccessLevel(mat.access_level)
      );
      if (firstAccessibleMaterial) {
        handleMaterialClick(firstAccessibleMaterial.id);
      }
    }
  }, [
    materialList,
    isOpen,
    selectedMaterialId,
    checkAccessLevel,
    handleMaterialClick,
    currentUser,
  ]);

  // Handle response for get-material-instance-list
  useEffect(() => {
    try {
      if (!isOpen) return;
      if (lastJsonMessage && hasProperty(lastJsonMessage, "requestID")) {
        const requestData = retrieveRequestData(lastJsonMessage.requestID);
        if (requestData && requestData.type === "get-material-instance-list") {
          // Handle warnings/errors similar to other responses
          if (
            Array.isArray(lastJsonMessage.Error) &&
            lastJsonMessage.Error.length > 0
          ) {
            recordedErrorLog(
              "Material instance list error:",
              lastJsonMessage.Error,
              true,
              "Backend Error"
            );
          }
          if (
            Array.isArray(lastJsonMessage.Warning) &&
            lastJsonMessage.Warning.length > 0
          ) {
            recordedErrorLog(
              "Material instance list warning:",
              lastJsonMessage.Warning
            );
            if (limitedToast) {
              limitedToast(
                "Received warnings from material instance list request"
              );
            }
          }

          const instances =
            hasProperty(lastJsonMessage, "Material") &&
            hasProperty(lastJsonMessage.Material, "MaterialInstanceList")
              ? lastJsonMessage.Material.MaterialInstanceList
              : [];
          setMaterialInstanceList(
            Array.isArray(instances) ? deepCopy(instances) : []
          );
          deleteRequestDataEntry(lastJsonMessage.requestID);
        }
      }
    } catch (error) {
      recordedErrorLog(
        "Error handling get-material-instance-list response:",
        error
      );
    }
  }, [
    lastJsonMessage,
    isOpen,
    retrieveRequestData,
    deleteRequestDataEntry,
    recordedErrorLog,
    limitedToast,
  ]);

  // Auto-select first material instance with proper access level when list loads
  useEffect(() => {
    if (
      isOpen &&
      selectedMaterialId !== null &&
      materialInstanceList.length > 0 &&
      selectedMaterialInstanceId === null &&
      checkAccessLevel &&
      currentUser &&
      currentUser.id
    ) {
      // Find first material instance with proper access level
      const sortedInstances = [...materialInstanceList].sort(
        (a, b) => (a.list_index ?? 0) - (b.list_index ?? 0)
      );
      const firstAccessibleInstance = sortedInstances.find(
        (instance) =>
          hasProperty(instance, "access_level") &&
          checkAccessLevel(instance.access_level)
      );
      if (firstAccessibleInstance) {
        handleInstanceClick(firstAccessibleInstance.id);
      }
    }
  }, [materialInstanceList]);

  // Handle response for get-validation-data-list
  useEffect(() => {
    try {
      if (!isOpen) return;
      if (lastJsonMessage && hasProperty(lastJsonMessage, "requestID")) {
        const requestData = retrieveRequestData(lastJsonMessage.requestID);
        if (requestData && requestData.type === "get-validation-data-list") {
          // Handle warnings/errors similar to other responses
          if (
            Array.isArray(lastJsonMessage.Error) &&
            lastJsonMessage.Error.length > 0
          ) {
            recordedErrorLog(
              "Validation data list error:",
              lastJsonMessage.Error,
              true,
              "Backend Error"
            );
          }
          if (
            Array.isArray(lastJsonMessage.Warning) &&
            lastJsonMessage.Warning.length > 0
          ) {
            recordedErrorLog(
              "Validation data list warning:",
              lastJsonMessage.Warning
            );
            if (limitedToast) {
              limitedToast(
                "Received warnings from validation data list request"
              );
            }
          }

          const validationData =
            hasProperty(lastJsonMessage, "Material") &&
            hasProperty(lastJsonMessage.Material, "ValidationDataList")
              ? lastJsonMessage.Material.ValidationDataList
              : [];
          setValidationDataList(
            Array.isArray(validationData) ? validationData : []
          );
          deleteRequestDataEntry(lastJsonMessage.requestID);
        }
      }
    } catch (error) {
      recordedErrorLog(
        "Error handling get-validation-data-list response:",
        error
      );
    }
  }, [
    lastJsonMessage,
    isOpen,
    retrieveRequestData,
    deleteRequestDataEntry,
    recordedErrorLog,
    limitedToast,
  ]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        shouldCloseOnOverlayClick={true}
        contentLabel="Material Database"
        appElement={appElement}
        id="materialDBModal"
        style={{
          content: {
            width: "80vw",
            height: "80vh",
            top: "10vh",
            left: "10vw",
            right: "auto",
            padding: "0",
            border: "1px solid #ccc",
            borderRadius: "8px",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: "9000",
          },
        }}
      >
        <div className="materialDBWindow">
          <div className="materialDBCloseButtonContainer">
            <CloseButton onClose={onClose} />
          </div>
          <div className="materialDBContent">
            <div className="materialDBContainer">
              <div className="listsColumn">
                <div className="listsTopSection">
                  <div className="materialDBSection materialSection">
                    <div className="sectionTitle">Select a Material:</div>
                    <div className="sectionList">
                      {materialList && materialList.length > 0 ? (
                        materialList
                          .sort(
                            (a, b) => (a.list_index ?? 0) - (b.list_index ?? 0)
                          )
                          .map((mat) => {
                            const hasAccess = checkAccessLevel(
                              mat.access_level ?? 1
                            );
                            return (
                              <div
                                key={mat.id}
                                className={`listEntry clickable ${
                                  !hasAccess ? "restricted" : ""
                                } ${
                                  selectedMaterialId === mat.id
                                    ? "selected"
                                    : ""
                                }`}
                                onClick={() => handleMaterialClick(mat.id)}
                              >
                                {mat.name}
                              </div>
                            );
                          })
                      ) : (
                        <div>No materials found.</div>
                      )}
                    </div>
                  </div>
                  <div className="materialDBSection instanceSection">
                    <div className="sectionTitle">Choose Specifications:</div>
                    <div className="sectionList">
                      {materialInstanceList &&
                      materialInstanceList.length > 0 ? (
                        materialInstanceList
                          .sort(
                            (a, b) => (a.list_index ?? 0) - (b.list_index ?? 0)
                          )
                          .map((instance) => {
                            const hasAccess = checkAccessLevel(
                              instance.access_level ?? 1
                            );
                            const entryContent = (
                              <div
                                key={instance.id}
                                className={`listEntry ${
                                  hasAccess ? "clickable" : "disabled"
                                } ${
                                  selectedMaterialInstanceId === instance.id &&
                                  hasAccess
                                    ? "selected"
                                    : ""
                                }`}
                                onClick={
                                  hasAccess
                                    ? () => handleInstanceClick(instance.id)
                                    : undefined
                                }
                              >
                                {instance.name}
                              </div>
                            );

                            if (!hasAccess) {
                              const tooltipMessage =
                                instance.access_level === 10
                                  ? "Please upgrade your subscription to Basic to access this material instance."
                                  : "Please upgrade your subscription to access this material instance.";

                              return (
                                <HtmlTooltip
                                  key={instance.id}
                                  title={
                                    <div className="customTooltip">
                                      <div className="tooltipContent">
                                        {tooltipMessage}
                                      </div>
                                    </div>
                                  }
                                >
                                  {entryContent}
                                </HtmlTooltip>
                              );
                            }

                            return entryContent;
                          })
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </div>
                <div className="listsBottomSection">
                  Our database is actively growing. Users are welcome to request
                  specific materials, and authors may propose their published
                  datasets for inclusion. All entries are referenced to the
                  original publication, offering greater visibility and citation
                  impact.
                </div>
              </div>
              <div className="rightColumn">
                <div className="materialDBSection metadataSection">
                  <div className="metadataColumns">
                    <div className="metadataColumn">
                      <div className="columnTitle">Material Properties:</div>
                      <div className="columnContent">
                        {selectedMaterialId &&
                          (() => {
                            const material = materialList.find(
                              (mat) => mat.id === selectedMaterialId
                            );
                            if (!material) return null;

                            const materialDisplay =
                              material.chemical_formula &&
                              material.chemical_formula !== "None"
                                ? material.chemical_formula
                                : material.name;

                            const instance = selectedMaterialInstanceId
                              ? materialInstanceList.find(
                                  (inst) =>
                                    inst.id === selectedMaterialInstanceId
                                )
                              : null;

                            return (
                              <>
                                <div className="infoGroup">
                                  <div>
                                    <strong>Material:</strong>
                                  </div>
                                  <div>{materialDisplay}</div>
                                </div>

                                {instance &&
                                  (() => {
                                    const bulkInfo =
                                      instance.is_bulk !== null &&
                                      instance.is_bulk !== undefined &&
                                      instance.is_bulk !== "None"
                                        ? instance.is_bulk
                                          ? "Bulk"
                                          : "Not Bulk"
                                        : null;

                                    const filmInfo =
                                      instance.is_film !== null &&
                                      instance.is_film !== undefined &&
                                      instance.is_film !== "None"
                                        ? instance.is_film
                                          ? "Thin Film"
                                          : "Not Thin Film"
                                        : null;

                                    const combined = [bulkInfo, filmInfo]
                                      .filter(Boolean)
                                      .join(", ");

                                    return combined ? (
                                      <div className="infoGroup">
                                        <div>
                                          <strong>{combined}</strong>
                                        </div>
                                      </div>
                                    ) : null;
                                  })()}

                                {instance &&
                                  instance.spectral_range_min !== null &&
                                  instance.spectral_range_min !== undefined &&
                                  instance.spectral_range_min !== "None" &&
                                  instance.spectral_range_max !== null &&
                                  instance.spectral_range_max !== undefined &&
                                  instance.spectral_range_max !== "None" &&
                                  instance.spectral_range_units !== null &&
                                  instance.spectral_range_units !== undefined &&
                                  instance.spectral_range_units !== "None" && (
                                    <div className="infoGroup">
                                      <div>
                                        <strong>Spectral Range:</strong>
                                      </div>
                                      <div>
                                        {instance.spectral_range_min} -{" "}
                                        {instance.spectral_range_max}{" "}
                                        {instance.spectral_range_units === "cm^-1" ? <>cm<sup>-1</sup></> : instance.spectral_range_units}
                                      </div>
                                    </div>
                                  )}

                                {instance &&
                                  instance.temperature !== null &&
                                  instance.temperature !== undefined &&
                                  instance.temperature !== "None" && (
                                    <div className="infoGroup">
                                      <div>
                                        <strong>Temperature:</strong>
                                      </div>
                                      <div>{instance.temperature} K</div>
                                    </div>
                                  )}

                                {instance &&
                                  instance.thickness !== null &&
                                  instance.thickness !== undefined &&
                                  instance.thickness !== "None" && (
                                    <div className="infoGroup">
                                      <div>
                                        <strong>Thickness:</strong>
                                      </div>
                                      <div>{instance.thickness}</div>
                                    </div>
                                  )}
                              </>
                            );
                          })()}
                      </div>
                    </div>
                    <div className="metadataColumn">
                      <div className="columnTitle">Main Use Cases:</div>
                      <div className="columnContent">
                        {selectedMaterialId &&
                          (() => {
                            const material = materialList.find(
                              (mat) => mat.id === selectedMaterialId
                            );
                            if (!material) return null;

                            const instance = selectedMaterialInstanceId
                              ? materialInstanceList.find(
                                  (inst) =>
                                    inst.id === selectedMaterialInstanceId
                                )
                              : null;

                            return (
                              <>
                                {material.cathegory &&
                                  material.cathegory !== "None" && (
                                    <div className="infoGroup">
                                      <div>
                                        <strong>Category:</strong>
                                      </div>
                                      <div>{material.cathegory}</div>
                                    </div>
                                  )}

                                {instance &&
                                  instance.main_applications !== null &&
                                  instance.main_applications !== undefined &&
                                  instance.main_applications !== "None" && (
                                    <div className="infoGroup">
                                      <div>
                                        <strong>Main Applications:</strong>
                                      </div>
                                      <div>{instance.main_applications}</div>
                                    </div>
                                  )}

                                {instance &&
                                  instance.other_info !== null &&
                                  instance.other_info !== undefined &&
                                  instance.other_info !== "None" && (
                                    <div className="infoGroup">
                                      <div>
                                        <strong>Other Information:</strong>
                                      </div>
                                      <div>{instance.other_info}</div>
                                    </div>
                                  )}
                              </>
                            );
                          })()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="validationSection">
                  <div className="validationTitle">Model Based On:</div>
                  <div className="validationContent">
                    {validationDataList && validationDataList.length > 0 ? (
                      validationDataList
                        .sort(
                          (a, b) => (a.list_index ?? 0) - (b.list_index ?? 0)
                        )
                        .map((validation) => {
                          const data = getValidationData(validation);
                          return (
                            <div
                              key={validation.id}
                              className="validationEntry clickable"
                              onClick={() => handleValidationClick(validation)}
                            >
                              <div>
                                <b>Type:</b> {processSymbols(data.quantityName)}
                                ,&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<b>
                                  Range:
                                </b>{" "}
                                {data.min} - {data.max} {data.units === "cm^-1" ? <>cm<sup>-1</sup></> : data.units}
                              </div>
                              {data.publication && (
                                <div>
                                  <b>Source:</b>{" "}
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: data.publication,
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="materialDBButtonContainer">
            <div className="materialDBDisclaimer">
              Please note that Speqqle does not guarantee the scientific
              accuracy of literature data or database models. These serve only
              as starting points and should be verified and refined by the user.
            </div>
            <CustomButton
              text="Load Model"
              handleClick={handleLoadModelFromMaterialDB}
              id="load-model-from-material-db"
              disabled={!selectedMaterialInstanceId}
            />
          </div>
        </div>
      </Modal>

      {/* Validation Detail Modal */}
      <Modal
        isOpen={validationDetailModalOpen}
        onRequestClose={handleCloseValidationDetail}
        shouldCloseOnOverlayClick={true}
        contentLabel="Validation Data Details"
        appElement={appElement}
        id="validationDetailModal"
        style={{
          content: {
            width: "60vw",
            height: "auto",
            maxHeight: "80vh",
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translate(-50%, -50%)",
            padding: "20px",
            border: "1px solid #ccc",
            borderRadius: "8px",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: "9001",
          },
        }}
      >
        {selectedValidation &&
          (() => {
            const data = getValidationData(selectedValidation);
            return (
              <div className="validationDetailWindow">
                <div className="validationDetailHeader">
                  <CustomButton
                    text="Load Dataset"
                    handleClick={handleLoadDataset}
                    id="load-dataset-button"
                  />
                  <CloseButton onClose={handleCloseValidationDetail} />
                </div>
                <div className="validationDetailContent">
                  <h2>{data.name}</h2>

                  <div className="validationDetailSection horizontal">
                    <div className="horizontalItem">
                      <strong>Type:</strong> {processSymbols(data.quantityName)}
                    </div>
                    <div className="horizontalItem">
                      <strong>Range:</strong> {data.min} - {data.max}{" "}
                      {data.units}
                    </div>
                    <div className="horizontalItem">
                      <strong>Number of Datapoints:</strong> {data.npoints}
                    </div>
                  </div>

                  {data.publication && (
                    <div className="validationDetailSection">
                      <div>
                        <strong>Source:</strong>
                      </div>
                      <div
                        dangerouslySetInnerHTML={{ __html: data.publication }}
                      />
                    </div>
                  )}

                  {selectedValidation.notes &&
                    selectedValidation.notes !== "None" && (
                      <div className="validationDetailSection">
                        <div>
                          <strong>Notes:</strong>
                        </div>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: processLinksForNewTab(
                              selectedValidation.notes
                            ),
                          }}
                        />
                      </div>
                    )}

                  {selectedValidation.other_info &&
                    selectedValidation.other_info !== "None" && (
                      <div className="validationDetailSection">
                        <div>
                          <strong>Other Information:</strong>
                        </div>
                        <div>{selectedValidation.other_info}</div>
                      </div>
                    )}
                </div>
              </div>
            );
          })()}
      </Modal>
    </>
  );
}
